/**
 * Os documentos do paciente (#811, clínica): identidade, carteirinha, termos
 * e contratos, separados dos documentos da oportunidade.
 *
 * Só nas organizações com a funcionalidade "clinica" ligada; sem ela, 404. O
 * arquivo abre por um link assinado, que vale cinco minutos e é emitido pela
 * listagem.
 */

import { z } from 'zod';

const InstanteSchema = z.string().datetime();

export const CategoriaDoDocumentoDoPacienteSchema = z.enum([
  'identidade',
  'carteirinha',
  'termo',
  'contrato',
  'outro',
]);
export type CategoriaDoDocumentoDoPaciente = z.infer<typeof CategoriaDoDocumentoDoPacienteSchema>;

export const DocumentoDoPacienteSchema = z
  .object({
    id: z.string().uuid(),
    nome: z.string(),
    /** Sem o ponto: `pdf`. Nula quando o nome não tem extensão. */
    extensao: z.string().nullable(),
    categoria: CategoriaDoDocumentoDoPacienteSchema,
    mime: z.string(),
    tamanho: z.number().int(),
    /** `AAAA-MM-DD`: até quando o documento vale (carteirinha, identidade). */
    validade: z
      .string()
      .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
      .nullable(),
    enviado_em: InstanteSchema,
    /** O link de abrir, assinado. Vale até `url_expira_em`. */
    url: z.string(),
    url_expira_em: InstanteSchema,
  })
  .strict();
export type DocumentoDoPaciente = z.infer<typeof DocumentoDoPacienteSchema>;

export const DocumentosDoPacienteSchema = z.object({
  /** Do mais novo ao mais antigo. */
  itens: z.array(DocumentoDoPacienteSchema),
});
export type DocumentosDoPaciente = z.infer<typeof DocumentosDoPacienteSchema>;

/**
 * Os campos do `multipart/form-data`, além dos arquivos (no campo `arquivos`):
 * a categoria de todos eles e, se houver, a validade.
 */
export const EnviarDocumentosDoPacienteSchema = z
  .object({
    categoria: CategoriaDoDocumentoDoPacienteSchema,
    validade: z
      .string()
      .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
      .optional(),
  })
  .strict();
export type EnviarDocumentosDoPaciente = z.infer<typeof EnviarDocumentosDoPacienteSchema>;

/** Até quantos arquivos por envio, e até quantos megabytes cada. */
export const LIMITES_DO_DOCUMENTO_DO_PACIENTE = {
  arquivosPorEnvio: 10,
  megabytesPorArquivo: 25,
} as const;
